import { useMemo } from "react";
import { useSearchParams, NavLink } from "react-router";

import { ANSWERED, INBOUND, OUTBOUND } from "@/constants";
import HeaderCard from "@/design/components/HeaderCard";
import Player from "@/design/components/Player";
import FormGroup from "@/design/forms/FormGroup";
import StringForm from "@/design/forms/StringForm";
import formatDuration from "@/utils/formaty";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import { formatCallerName } from "@/utils/utility";
import Valkyrie, {
  viArrowRight,
  viSparkles,
  viArrowRightProhibited,
  viPhoneArrowDownLeft,
  viPhoneArrowDownLeftProhibited,
  viPhoneArrowUpRight,
  viPhoneArrowUpRightProhibited,
} from "@sippy-platform/valkyrie";

import { Alert, Button, CardHeader, Dialog, Stack } from "@mui/material";
import { cn } from "cn";
import { format } from "date-fns";

import DataCell from "../_Components/DataCell";
import LogIcon from "../_Components/LogIcon";
import TableRow from "../_Components/TableRow";
import { transcriptParser } from "./transcriptParser";
import { TranscriptPlayer } from "./TranscriptPlayer";

export default function CallItem({
  callLogEvent,
}: {
  callLogEvent: {
    id: string;
    blocked: boolean;
    direction: "INTERN" | "OUTBOUND" | "INBOUND";
    duration: number;
    disposition: string;
    dst: string;
    src: string;
    billsec: number;
    calldate: Date;
    clid: string;
    userfield: string;
    aiAnalysis?: {
      language: string;
      summary: string;
      transcription: string;
      calleeMood: string;
      callerMood: string;
    };
    answeredBy: string;
    firebaseUrl: string[];
  };
}) {
  const colors = useAccessibleColors();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentSearchId = searchParams.get("id");
  const callLogEventId = String(callLogEvent.id);
  const isDialogOpen = currentSearchId === callLogEventId;

  const linkSearchParams = useMemo(() => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("id", callLogEventId);

    return nextSearchParams.toString();
  }, [searchParams, callLogEventId]);

  const handleClose = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("id");
    setSearchParams(nextSearchParams);
  };

  const color = callLogEvent.blocked ? colors.amber : callLogEvent.disposition === ANSWERED ? colors.green : colors.red;

  const { rawTranscriptionWithLineBreaks, transcript } = useMemo(() => {
    if (!callLogEvent.aiAnalysis || !isDialogOpen) {
      return {
        rawTranscriptionWithLineBreaks: "",
        transcript: {
          rawTranscript: "",
          steps: [],
          failed: true,
        },
      };
    }

    const rawTranscription = callLogEvent.aiAnalysis.transcription || "";

    return {
      rawTranscriptionWithLineBreaks: rawTranscription.replace(/(?!^)\s*\[/g, "\n["),
      transcript: transcriptParser(rawTranscription),
    };
  }, [callLogEvent.aiAnalysis, isDialogOpen]);

  return (
    <>
      <TableRow>
        <Stack sx={{ pr: "0 !important" }}>
          {callLogEvent.direction === INBOUND ? (
            callLogEvent.blocked ? (
              <LogIcon title="Blocked incoming call" icon={viPhoneArrowDownLeftProhibited} color={color} />
            ) : (
              <LogIcon title="Incoming call" icon={viPhoneArrowDownLeft} color={color} />
            )
          ) : callLogEvent.direction === OUTBOUND ? (
            callLogEvent.blocked ? (
              <LogIcon title="Blocked outgoing call" icon={viPhoneArrowUpRightProhibited} color={color} />
            ) : (
              <LogIcon title="Outgoing call" icon={viPhoneArrowUpRight} color={color} />
            )
          ) : callLogEvent.blocked ? (
            <LogIcon title="Blocked internal call" icon={viArrowRightProhibited} color={color} />
          ) : (
            <LogIcon title="Internal call" icon={viArrowRight} color={color} />
          )}
        </Stack>
        <DataCell
          invertOrder
          noWrap
          tabular
          primary={format(callLogEvent.calldate, "pp")}
          secondary={format(callLogEvent.calldate, "PPP")}
        />
        <DataCell noWrap primary={formatCallerName(callLogEvent.clid)} />
        <DataCell primary={callLogEvent.src} />
        <DataCell
          noWrap
          primary={callLogEvent.dst}
          secondary={
            callLogEvent.answeredBy && callLogEvent.dst !== callLogEvent.answeredBy
              ? `Answered by ${callLogEvent.answeredBy}`
              : undefined
          }
        />
        <DataCell tabular align="right" primary={formatDuration(callLogEvent.duration - callLogEvent.billsec)} />
        <DataCell tabular align="right" primary={formatDuration(callLogEvent.billsec)} />
        <Stack
          direction="row"
          sx={{
            gap: 0.5,
            my: -1,
            flexWrap: "nowrap",
          }}
        >
          {callLogEvent.firebaseUrl?.map((url) => (
            <Player src={url} key={url} />
          ))}
        </Stack>
        <Stack direction="row">
          <Button
            component={NavLink}
            to={`?${linkSearchParams}`}
            size="small"
            color="inherit"
            endIcon={callLogEvent.aiAnalysis && <Valkyrie icon={viSparkles} style={{ fontSize: 16 }} />}
          >
            Details
          </Button>
        </Stack>
      </TableRow>
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        maxWidth="md"
        slotProps={{ paper: { id: "callDetailsDialog" } }}
      >
        <HeaderCard>
          <CardHeader
            avatar={
              callLogEvent.direction === INBOUND ? (
                callLogEvent.blocked ? (
                  <LogIcon title="Blocked incoming call" icon={viPhoneArrowDownLeftProhibited} color={color} />
                ) : (
                  <LogIcon title="Incoming call" icon={viPhoneArrowDownLeft} color={color} />
                )
              ) : callLogEvent.direction === OUTBOUND ? (
                callLogEvent.blocked ? (
                  <LogIcon title="Blocked outgoing call" icon={viPhoneArrowUpRightProhibited} color={color} />
                ) : (
                  <LogIcon title="Outgoing call" icon={viPhoneArrowUpRight} color={color} />
                )
              ) : callLogEvent.blocked ? (
                <LogIcon title="Blocked internal call" icon={viArrowRightProhibited} color={color} />
              ) : (
                <LogIcon title="Internal call" icon={viArrowRight} color={color} />
              )
            }
            title={format(callLogEvent.calldate, "d MMMM yyyy, HH:mm:ss")}
          />
        </HeaderCard>
        <Stack
          sx={{
            gap: 2,
            mt: 2,
          }}
        >
          <FormGroup title="About this call" tailwindApi>
            <StringForm
              label="Date"
              text={format(callLogEvent.calldate, "d MMMM yyyy, HH:mm:ss")}
              className={callLogEvent.aiAnalysis ? "" : "col-span-full"}
            />
            {callLogEvent.aiAnalysis && <StringForm label="Language" text={callLogEvent.aiAnalysis.language} />}
            {callLogEvent.aiAnalysis && (
              <StringForm label="Summary" text={callLogEvent.aiAnalysis.summary} className="col-span-full" />
            )}
            <StringForm label="Ring time" text={formatDuration(callLogEvent.duration - callLogEvent.billsec)} />
            <StringForm label="Talk time" text={formatDuration(callLogEvent.billsec)} />
            <StringForm
              label="Caller"
              text={
                <>
                  <DataCell noWrap primary={formatCallerName(callLogEvent.clid)} secondary={callLogEvent.src} />
                  {callLogEvent.aiAnalysis && <p className="mt-3 text-sm">{callLogEvent.aiAnalysis.callerMood}</p>}
                </>
              }
            />
            <StringForm
              label="Callee"
              text={
                <>
                  <DataCell
                    noWrap
                    primary={callLogEvent.dst}
                    secondary={
                      callLogEvent.answeredBy && callLogEvent.dst !== callLogEvent.answeredBy
                        ? `Answered by ${callLogEvent.answeredBy}`
                        : undefined
                    }
                  />
                  {callLogEvent.aiAnalysis && <p className="mt-3 text-sm">{callLogEvent.aiAnalysis.calleeMood}</p>}
                </>
              }
            />
          </FormGroup>
          {((callLogEvent.firebaseUrl?.length ?? 0) > 0 || callLogEvent.aiAnalysis) && (
            <FormGroup title="Playback" tailwindApi>
              <div className="col-span-full">
                {callLogEvent.firebaseUrl?.map((url) => (
                  <TranscriptPlayer src={url} key={url} transcript={transcript} />
                ))}
              </div>
              {callLogEvent.aiAnalysis && (
                <div className="col-span-full">
                  <Stack sx={{ gap: 0.5 }}>
                    {transcript.failed ? (
                      <>
                        <Alert severity="warning">
                          We couldn't successfully parse the transcription into steps. Below follows the raw transcript
                          instead.
                        </Alert>
                        <div className="rounded-sm bg-zinc-100 px-2 py-1 text-sm whitespace-pre-wrap">
                          {rawTranscriptionWithLineBreaks}
                        </div>
                      </>
                    ) : (
                      <div className="gap-x-4 @max-lg/group:flex @max-lg/group:flex-col @max-lg/group:gap-y-1 @lg/group:grid @lg/group:grid-cols-[min-content_min-content_auto] @lg/group:gap-y-0.5">
                        {transcript.steps.map((step, index) => {
                          const participantColorIndex = ((step.speakerId - 1) % 10) + 1;

                          return (
                            <div
                              className={cn(
                                "grid rounded-md text-start odd:bg-zinc-50 hover:bg-zinc-100 dark:odd:bg-zinc-900 dark:hover:bg-zinc-950",
                                "@lg/group:col-span-full @lg/group:grid-cols-subgrid @lg/group:items-start @lg/group:px-2 @lg/group:py-1",
                                "@max-lg/group:grid-cols-[min-content_auto] @max-lg/group:gap-x-3 @max-lg/group:gap-y-1 @max-lg/group:px-2 @max-lg/group:pt-1 @max-lg/group:pb-1.5",
                              )}
                              key={index}
                            >
                              <span className="inline-flex">
                                <span
                                  className={cn("inline bg-transparent! text-sm/5 font-semibold text-nowrap", {
                                    "text-lime-600 dark:text-lime-400": participantColorIndex === 1,
                                    "text-sky-600 dark:text-sky-400": participantColorIndex === 2,
                                    "text-amber-600 dark:text-amber-400": participantColorIndex === 3,
                                    "text-fuchsia-600 dark:text-fuchsia-400": participantColorIndex === 4,
                                    "text-red-600 dark:text-red-400": participantColorIndex === 5,
                                    "text-violet-600 dark:text-violet-400": participantColorIndex === 6,
                                    "text-green-600 dark:text-green-400": participantColorIndex === 7,
                                    "text-yellow-600 dark:text-yellow-400": participantColorIndex === 8,
                                    "text-blue-600 dark:text-blue-400": participantColorIndex === 9,
                                    "text-pink-600 dark:text-pink-400": participantColorIndex === 10,
                                  })}
                                >
                                  {step.speaker}
                                </span>
                              </span>
                              <span className="text-end text-sm/5 text-zinc-600 tabular-nums @max-lg/group:text-end dark:text-zinc-400">
                                {formatDuration(step.timecode)}
                              </span>
                              <span className="text-sm/4.5 @max-lg/group:col-span-full">{step.message}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Stack>
                </div>
              )}
            </FormGroup>
          )}
          {callLogEvent.aiAnalysis && (
            <Alert severity="info" sx={{ boxShadow: 1 }} icon={<Valkyrie icon={viSparkles} />}>
              The summary, caller and callee mood, and transcript are generated by AI and may contain errors.
            </Alert>
          )}
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-end",
              gap: 1,
              containerType: "inline-size",
            }}
          >
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}
